import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { streamPost } from "@/lib/stream";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AiText from "@/components/AiText";
import TeacherAvatar from "@/components/TeacherAvatar";
import { Send, Mic, Square, Volume2, Image as ImageIcon, Loader2 } from "lucide-react";

const CHIPS = [
  "Kesirleri nasıl toplarım?",
  "Özne nedir, örnek verir misin?",
  "Güneş neden batar?",
  "Present Simple ne demek?",
];

function playBase64Audio(b64) {
  if (!b64) return;
  const audio = new Audio(`data:audio/mp3;base64,${b64}`);
  audio.play().catch(() => {});
}

export default function AskAI() {
  const navigate = useNavigate();
  const [sessionId] = useState(() => localStorage.getItem("nd_ask_session") || (() => {
    const s = "ask_" + Math.random().toString(36).slice(2, 10);
    localStorage.setItem("nd_ask_session", s);
    return s;
  })());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [diagramBusy, setDiagramBusy] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const endRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    api.get(`/ai/chat/${sessionId}`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [sessionId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const gate = (err) => {
    if (err.status === 402 || err.response?.status === 402) {
      toast.error("Bu özellik için Premium abonelik gerekli");
      navigate("/abonelik");
      return true;
    }
    return false;
  };

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    try {
      await streamPost("/ai/ask", { session_id: sessionId, question }, (delta) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
          return copy;
        });
      });
    } catch (err) {
      if (!gate(err)) toast.error(err.message || "Bir hata oluştu");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await sendVoice(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Mikrofona erişilemedi. İzin verdiğinden emin ol.");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  const sendVoice = async (blob) => {
    setVoiceBusy(true);
    const fd = new FormData();
    fd.append("session_id", sessionId);
    fd.append("audio", blob, "soru.webm");
    try {
      const { data } = await api.post("/ai/voice-ask", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages((m) => [
        ...m,
        { role: "user", content: data.question_text },
        { role: "assistant", content: data.answer_text, audio: data.audio_base64 },
      ]);
      playBase64Audio(data.audio_base64);
    } catch (err) {
      if (!gate(err)) toast.error(err.response?.data?.detail || "Sesli soru gönderilemedi");
    } finally {
      setVoiceBusy(false);
    }
  };

  const speak = async (idx, text) => {
    setSpeakingIdx(idx);
    try {
      const { data } = await api.post("/ai/speak", { prompt: text });
      playBase64Audio(data.audio_base64);
    } catch (err) {
      if (!gate(err)) toast.error("Ses oluşturulamadı");
    } finally {
      setSpeakingIdx(null);
    }
  };

  const makeDiagram = async () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    const prompt = (input.trim() || lastUser?.content || "").trim();
    if (!prompt) { toast.error("Önce bir konu yaz ya da soru sor"); return; }
    setDiagramBusy(true);
    setMessages((m) => [...m, { role: "assistant", content: "🎨 Görsel hazırlanıyor...", pending: true }]);
    try {
      const { data } = await api.post("/ai/diagram", { prompt });
      setMessages((m) => {
        const copy = m.filter((x) => !x.pending);
        return [...copy, { role: "assistant", content: data.caption || "İşte senin için hazırladığım görsel! 🎨", image: data.image }];
      });
    } catch (err) {
      setMessages((m) => m.filter((x) => !x.pending));
      if (!gate(err)) toast.error(err.response?.data?.detail || "Görsel oluşturulamadı");
    } finally {
      setDiagramBusy(false);
    }
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant" && !m.pending);

  return (
    <div className="max-w-3xl mx-auto flex flex-col" style={{ height: "calc(100vh - 12rem)" }}>
      <TeacherAvatar
        message={lastAssistant?.content}
        typing={busy || voiceBusy || diagramBusy}
        idle="Merhaba! Ben Nokta Öğretmen 👋 Bir soru sor ya da mikrofona bas, hemen yanıtlayayım!"
      />
      <div className="mb-4">
        <h1 className="font-display text-2xl font-bold text-slate-900">💬 Soru-Cevap</h1>
        <p className="text-slate-500 text-sm">Yazarak ya da 🎤 sesli sorabilirsin — Nokta Öğretmen yanıtlar!</p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-10">
            <img src="/teacher-avatar.png" alt="Nokta Öğretmen" className="w-24 h-24 mx-auto mb-3 object-cover object-top rounded-full ring-4 ring-rose-100" />
            <p className="text-slate-600 font-semibold">Merhaba! Ben Nokta Öğretmen.</p>
            <p className="text-slate-400 text-sm mb-5">Yaz, sesli sor ya da görsel iste 👇</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {CHIPS.map((c) => (
                <button key={c} data-testid="suggestion-chip" onClick={() => send(c)} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm text-slate-600 hover:border-indigo-300 hover:text-indigo-600">
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} animate-float-up`}>
            {m.role === "assistant" && <img src="/teacher-avatar.png" alt="" className="w-8 h-8 mr-2 shrink-0 object-cover object-top rounded-full ring-2 ring-rose-100" />}
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-indigo-600 text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
              {m.role === "user" ? <p className="text-sm">{m.content}</p> : (
                <>
                  <AiText text={m.content || "..."} />
                  {m.image && (
                    <img src={m.image} alt="Görsel açıklama" data-testid={`ai-diagram-${i}`} className="mt-3 rounded-xl border border-slate-200 max-w-full" />
                  )}
                  {!m.pending && m.content && (
                    <button data-testid={`speak-btn-${i}`} onClick={() => speak(i, m.content)} disabled={speakingIdx === i}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 px-3.5 py-2 rounded-xl shadow-sm transition-colors">
                      {speakingIdx === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />} Sesli dinle
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {(recording || voiceBusy) && (
        <div className="mb-2 text-center text-sm font-semibold text-rose-600" data-testid="voice-status">
          {recording ? "🔴 Dinliyorum... bitince mikrofona tekrar bas" : "Sesin yazıya çevriliyor..."}
        </div>
      )}

      <div className="pt-3 border-t border-slate-200 bg-slate-50 space-y-2">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
          <Button
            type="button"
            data-testid="mic-btn"
            onClick={recording ? stopRecording : startRecording}
            disabled={busy || voiceBusy}
            className={`rounded-xl px-4 font-bold gap-1.5 shrink-0 ${recording ? "bg-rose-500 hover:bg-rose-600 animate-pulse" : "bg-emerald-500 hover:bg-emerald-600"}`}
          >
            {recording ? <><Square className="w-5 h-5" /> Durdur</> : <><Mic className="w-5 h-5" /> Sesli Sor</>}
          </Button>
          <Input data-testid="ask-ai-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Sorunu yaz ya da mikrofona bas..." className="rounded-xl flex-1 min-w-0" disabled={busy} />
          <Button data-testid="send-question-btn" type="submit" disabled={busy || !input.trim()} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl px-5">
            <Send className="w-5 h-5" />
          </Button>
        </form>
        <Button type="button" data-testid="diagram-btn" onClick={makeDiagram} disabled={diagramBusy} variant="outline" className="w-full rounded-xl border-2 font-bold text-indigo-600 border-indigo-200">
          {diagramBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-1" />} Görsel Oluştur (şekil / tablo)
        </Button>
      </div>
    </div>
  );
}
